import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaProjectDiagram, FaUsers, FaTasks, FaCalendar, FaChevronRight, FaSpinner, FaCheckCircle, FaTimes, FaInfoCircle, FaUser } from 'react-icons/fa';
import { fetchAuth } from '../../utils/api';

export default function JefeProyectos() {
  const navigate = useNavigate();
  const [proyectos, setProyectos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProyecto, setSelectedProyecto] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalLoading] = useState(false);
  const [detalleProyecto, setDetalleProyecto] = useState(null);
  const [modalExpanded, setModalExpanded] = useState(false);

  const toUIEstado = (estado) => {
    switch (estado) {
      case 'PENDIENTE': return 'Pendiente';
      case 'ACTIVO': return 'En Progreso';
      case 'RECHAZADO': return 'Cancelado';
      default: return estado || 'Pendiente';
    }
  };

  useEffect(() => {
    const cargarProyectos = async () => {
      try {
        setLoading(true);
        const data = await fetchAuth('/api/jefe/proyectos/');
        const proyectosFormateados = (data.proyectos || []).map(proyecto => ({
          id_proyecto: proyecto.id_proyecto,
          nombre_proyecto: proyecto.nombre_proyecto,
          descripcion: proyecto.descripcion,
          fecha_asignacion: proyecto.fecha_inicio,
          estado: toUIEstado(proyecto.estado),
          colaboradores: proyecto.colaboradores || [],
          colaboradores_activos: Number(proyecto.colaboradores_activos || (Array.isArray(proyecto.colaboradores) ? proyecto.colaboradores.length : 0)),
          tareas_completadas: proyecto.tareas_completadas || 0,
          tareas_totales: proyecto.tareas_totales || 0
        }));
        
        // Filtrar solo proyectos en progreso
        const activos = proyectosFormateados.filter(p => p.estado === 'En Progreso');
        setProyectos(activos);
      } catch (err) {
        console.error('Error al cargar proyectos:', err);
        setError(err.message || 'Error al cargar datos');
      } finally {
        setLoading(false);
      }
    };
    
    cargarProyectos();
  }, []);

  const calcularProgreso = (completadas, totales) => {
    if (totales === 0) return 0;
    return Math.round((completadas / totales) * 100);
  };

  const handleVerDetalle = (proyecto) => {
    navigate(`/app/jefe/proyectos/${proyecto.id_proyecto}`);
  };

  const cerrarModal = () => {
    setShowModal(false);
    setSelectedProyecto(null);
    setDetalleProyecto(null);
    setModalExpanded(false);
  };

  const toggleExpandModal = () => {
    setModalExpanded(!modalExpanded);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <FaSpinner className="animate-spin text-5xl text-slate-600 mx-auto mb-4" />
          <p className="text-slate-600 text-lg font-medium">Cargando proyectos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-8 max-w-md">
          <div className="text-red-600 text-5xl mb-4">⚠️</div>
          <h3 className="text-red-800 text-xl font-bold mb-2">Error al cargar</h3>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-slate-100 p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center gap-4 mb-3">
          <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 p-3 rounded-2xl shadow-lg">
            <FaProjectDiagram className="text-white text-2xl" />
          </div>
          <div>
            <h1 className="text-4xl font-bold text-slate-800 tracking-tight">
              Mis Proyectos
            </h1>
            <p className="text-slate-600 mt-1">
              Proyectos aceptados y en progreso
            </p>
          </div>
        </div>
        
        {/* Stats */}
        {proyectos.length > 0 && (
          <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full font-semibold text-sm mt-4">
            <FaCheckCircle className="text-indigo-500" />
            {proyectos.length} {proyectos.length === 1 ? 'proyecto activo' : 'proyectos activos'}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto">
        {proyectos.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-xl p-16 text-center border border-slate-200">
            <div className="bg-slate-100 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaProjectDiagram className="text-slate-400 text-4xl" />
            </div>
            <h3 className="text-2xl font-bold text-slate-800 mb-2">
              No tienes proyectos activos
            </h3>
            <p className="text-slate-600 max-w-md mx-auto">
              Cuando aceptes invitaciones de proyectos, aparecerán aquí para que puedas gestionarlos.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {proyectos.map((proyecto) => {
              const progreso = calcularProgreso(proyecto.tareas_completadas, proyecto.tareas_totales);
              
              return (
                <div
                  key={proyecto.id_proyecto}
                  onClick={() => handleVerDetalle(proyecto)}
                  className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-slate-200 overflow-hidden group cursor-pointer"
                >
                  {/* Card Header */}
                  <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 p-6 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -mr-24 -mt-24"></div>
                    <div className="relative">
                      <div className="flex items-center justify-between mb-3">
                        <div className="bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full">
                          <span className="text-white text-xs font-semibold">En Progreso</span>
                        </div>
                        <FaChevronRight className="text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all duration-200" />
                      </div>
                      <h3 className="text-xl font-bold text-white line-clamp-2 min-h-[3.5rem]">
                        {proyecto.nombre_proyecto}
                      </h3>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-6">
                    <p className="text-slate-600 text-sm leading-relaxed mb-6 line-clamp-2 min-h-[2.5rem]">
                      {proyecto.descripcion || 'Sin descripción disponible'}
                    </p>

                    {/* Progress Bar */}
                    {proyecto.tareas_totales > 0 && (
                      <div className="mb-6">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-xs font-semibold text-slate-600 flex items-center gap-1.5">
                            <FaTasks className="text-slate-400" />
                            Progreso de tareas
                          </span>
                          <span className="text-xs font-bold text-indigo-600">{progreso}%</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                          <div
                            className="bg-gradient-to-r from-indigo-500 to-indigo-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${progreso}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          {proyecto.tareas_completadas} de {proyecto.tareas_totales} completadas
                        </p>
                      </div>
                    )}

                    {/* Info Grid */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 text-sm bg-slate-50 rounded-xl p-3">
                        <FaCalendar className="text-slate-400 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-slate-500">Fecha de inicio</p>
                          <p className="font-semibold text-slate-700">
                            {formatDate(proyecto.fecha_asignacion)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 text-sm bg-slate-50 rounded-xl p-3">
                        <FaUsers className="text-slate-400 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-slate-500">Colaboradores Activos</p>
                          <p className="font-semibold text-slate-700">
                            {proyecto.colaboradores_activos} {proyecto.colaboradores_activos === 1 ? 'miembro' : 'miembros'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Action Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleVerDetalle(proyecto);
                      }}
                      className="w-full mt-6 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-indigo-500/30 hover:shadow-xl group"
                    >
                      <span>Ver Detalle</span>
                      <FaChevronRight className="group-hover:translate-x-1 transition-transform duration-200" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal de Detalle */}
      {showModal && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={cerrarModal}
        >
          <div 
            className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 p-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -mr-48 -mt-48"></div>
              <div className="relative flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <FaProjectDiagram className="text-white text-2xl" />
                    <h2 className="text-2xl font-bold text-white">
                      {selectedProyecto?.nombre_proyecto}
                    </h2>
                  </div>
                  <p className="text-white/80 text-sm">
                    {selectedProyecto?.descripcion || 'Sin descripción'}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={toggleExpandModal}
                    className="bg-white/20 hover:bg-white/30 p-2 rounded-xl transition-all transform hover:scale-110"
                    title={modalExpanded ? "Ver menos" : "Ver más detalles"}
                  >
                    <FaInfoCircle className={`text-white text-xl transition-transform duration-300 ${modalExpanded ? 'rotate-180' : ''}`} />
                  </button>
                  <button
                    onClick={cerrarModal}
                    className="bg-white/20 hover:bg-white/30 p-2 rounded-xl transition-colors"
                  >
                    <FaTimes className="text-white text-xl" />
                  </button>
                </div>
              </div>
            </div>

            {/* Modal Body con animación de expansión */}
            <div className={`overflow-y-auto transition-all duration-500 ease-in-out ${
              modalExpanded ? 'max-h-[70vh]' : 'max-h-[50vh]'
            }`}>
              {modalLoading ? (
                <div className="flex items-center justify-center py-12">
                  <FaSpinner className="animate-spin text-4xl text-indigo-500" />
                </div>
              ) : detalleProyecto ? (
                <div className="space-y-6 p-6">
                  {/* Info Cards Grid - Siempre visible */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-5 border border-blue-200">
                      <div className="flex items-center gap-3 mb-2">
                        <FaCalendar className="text-blue-600 text-xl" />
                        <h3 className="font-bold text-blue-900">Fecha de Inicio</h3>
                      </div>
                      <p className="text-blue-700 text-lg font-semibold">
                        {formatDate(selectedProyecto.fecha_asignacion)}
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-5 border border-purple-200">
                      <div className="flex items-center gap-3 mb-2">
                        <FaTasks className="text-purple-600 text-xl" />
                        <h3 className="font-bold text-purple-900">Estado de Tareas</h3>
                      </div>
                      <p className="text-purple-700 text-lg font-semibold">
                        {selectedProyecto.tareas_completadas} / {selectedProyecto.tareas_totales} completadas
                      </p>
                      <div className="w-full bg-purple-200 rounded-full h-2 mt-2">
                        <div
                          className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${calcularProgreso(selectedProyecto.tareas_completadas, selectedProyecto.tareas_totales)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  {/* Botón para expandir/contraer */}
                  {(detalleProyecto.colaboradores?.length > 0 || detalleProyecto.tareas?.length > 0 || detalleProyecto.descripcion_completa) && (
                    <button
                      onClick={toggleExpandModal}
                      className="w-full bg-gradient-to-r from-indigo-50 to-indigo-100 hover:from-indigo-100 hover:to-indigo-200 text-indigo-700 font-semibold py-3 px-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 border border-indigo-200"
                    >
                      <FaInfoCircle className={`transition-transform duration-300 ${modalExpanded ? 'rotate-180' : ''}`} />
                      <span>{modalExpanded ? 'Ver menos detalles' : 'Ver más detalles'}</span>
                      <FaChevronRight className={`transition-transform duration-300 ${modalExpanded ? 'rotate-90' : ''}`} />
                    </button>
                  )}

                  {/* Sección expandible con animación */}
                  <div className={`space-y-6 transition-all duration-500 ease-in-out overflow-hidden ${
                    modalExpanded 
                      ? 'max-h-[2000px] opacity-100 mt-6' 
                      : 'max-h-0 opacity-0 mt-0'
                  }`}>
                    {/* Colaboradores */}
                    {detalleProyecto.colaboradores && detalleProyecto.colaboradores.length > 0 && (
                    <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
                      <div className="flex items-center gap-3 mb-4">
                        <FaUsers className="text-slate-600 text-2xl" />
                        <h3 className="text-xl font-bold text-slate-800">Colaboradores</h3>
                        <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm font-semibold">
                          {detalleProyecto.colaboradores.length}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {detalleProyecto.colaboradores.map((colab, index) => (
                          <div key={index} className="bg-white rounded-xl p-4 flex items-center gap-3 border border-slate-200 hover:border-indigo-300 transition-colors">
                            <div className="bg-indigo-100 p-3 rounded-full">
                              <FaUser className="text-indigo-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-slate-800">{colab.nombre || 'Colaborador'}</p>
                              <p className="text-sm text-slate-500">{colab.email || 'Sin email'}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Tareas */}
                  {detalleProyecto.tareas && detalleProyecto.tareas.length > 0 && (
                    <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
                      <div className="flex items-center gap-3 mb-4">
                        <FaTasks className="text-slate-600 text-2xl" />
                        <h3 className="text-xl font-bold text-slate-800">Tareas del Proyecto</h3>
                      </div>
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {detalleProyecto.tareas.map((tarea, index) => (
                          <div key={index} className="bg-white rounded-xl p-4 border border-slate-200">
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-semibold text-slate-800">{tarea.nombre || 'Tarea sin nombre'}</h4>
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                tarea.estado === 'COMPLETADA' ? 'bg-green-100 text-green-700' :
                                tarea.estado === 'EN_PROGRESO' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {tarea.estado?.replace('_', ' ')}
                              </span>
                            </div>
                            <p className="text-sm text-slate-600">{tarea.descripcion || 'Sin descripción'}</p>
                            {tarea.asignado_a && (
                              <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
                                <FaUser className="text-slate-400" />
                                <span>Asignado a: {tarea.asignado_a}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                    {/* Descripción Completa */}
                    {detalleProyecto.descripcion_completa && (
                      <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
                        <div className="flex items-center gap-3 mb-4">
                          <FaInfoCircle className="text-slate-600 text-2xl" />
                          <h3 className="text-xl font-bold text-slate-800">Información Adicional</h3>
                        </div>
                        <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {detalleProyecto.descripcion_completa}
                        </p>
                      </div>
                    )}
                  </div>
                  {/* FIN Sección expandible */}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-slate-600">No se pudieron cargar los detalles del proyecto</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-slate-50 p-6 border-t border-slate-200 flex justify-end">
              <button
                onClick={cerrarModal}
                className="px-6 py-3 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold rounded-xl transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
  const formatDate = (s) => {
    const a = String(s || '')
    const part = a.split('T')[0].split(' ')[0]
    if (!part) return '-'
    const [y, m, d] = part.split('-')
    return `${d}/${m}/${y}`
  }